// Tynn fetch-wrapper. Cookies følger med (credentials: include) for auth.
const BASE = "";

async function req<T>(path: string, opts: RequestInit = {}): Promise<T> {
  const res = await fetch(BASE + path, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(opts.headers ?? {}) },
    ...opts,
  });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      if (body?.error) msg = body.error;
    } catch {
      /* ignore */
    }
    throw new ApiError(msg, res.status);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

export type Me = { id: string; email: string };
export type Project = {
  id: string;
  name: string;
  public_key: string;
  secret_key?: string;
  created_at: string;
  role: string;
};
export type Stats = {
  range: string;
  summary: { pageviews: number; visitors: number };
  timeseries: { t: string; pageviews: number; visitors: number }[];
  topPages: { path: string; views: number }[];
  topSources: { source: string; visitors: number }[];
  topCountries: { country: string; visitors: number }[];
  browsers: { browser: string; visitors: number }[];
  devices: { device: string; visitors: number }[];
  topEvents: { name: string; count: number }[];
};

export const api = {
  me: () => req<Me>("/api/auth/me"),
  login: (email: string, password: string) =>
    req<Me>("/api/auth/login", { method: "POST", body: JSON.stringify({ email, password }) }),
  logout: () => req<{ ok: true }>("/api/auth/logout", { method: "POST" }),

  projects: () => req<Project[]>("/api/projects"),
  project: (id: string) => req<Project>(`/api/projects/${id}`),
  createProject: (name: string) =>
    req<Project>("/api/projects", { method: "POST", body: JSON.stringify({ name }) }),
  rotateKeys: (id: string) =>
    req<{ public_key: string; secret_key: string }>(`/api/projects/${id}/rotate-keys`, {
      method: "POST",
    }),
  deleteProject: (id: string) => req<{ ok: true }>(`/api/projects/${id}`, { method: "DELETE" }),

  stats: (id: string, range: string) =>
    req<Stats>(`/api/projects/${id}/stats?range=${encodeURIComponent(range)}`),

  issues: (id: string, status?: string, sort = "last_seen") =>
    req<IssueRow[]>(
      `/api/projects/${id}/issues?sort=${sort}${status ? `&status=${status}` : ""}`,
    ),
  issue: (gid: string) => req<IssueDetail>(`/api/issues/${gid}`),
  setIssueStatus: (gid: string, status: string) =>
    req<{ ok: true; status: string }>(`/api/issues/${gid}`, {
      method: "PATCH",
      body: JSON.stringify({ status }),
    }),

  // --- Fase 3 ---
  releases: (id: string) => req<ReleaseRow[]>(`/api/projects/${id}/releases`),

  monitors: (id: string) => req<Monitor[]>(`/api/projects/${id}/monitors`),
  createMonitor: (id: string, body: NewMonitor) =>
    req<{ id: string }>(`/api/projects/${id}/monitors`, { method: "POST", body: JSON.stringify(body) }),
  deleteMonitor: (mid: string) => req<{ ok: true }>(`/api/monitors/${mid}`, { method: "DELETE" }),

  funnels: (id: string) => req<Funnel[]>(`/api/projects/${id}/funnels`),
  createFunnel: (id: string, body: { name: string; steps: FunnelStep[] }) =>
    req<{ id: string }>(`/api/projects/${id}/funnels`, { method: "POST", body: JSON.stringify(body) }),
  deleteFunnel: (fid: string) => req<{ ok: true }>(`/api/funnels/${fid}`, { method: "DELETE" }),
  funnelResults: (fid: string, range: string) =>
    req<FunnelResult>(`/api/funnels/${fid}/results?range=${range}`),

  // --- Fase 4 ---
  vitals: (id: string, range: string) =>
    req<VitalsResult>(`/api/projects/${id}/vitals?range=${encodeURIComponent(range)}`),
  realtime: (id: string) => req<Realtime>(`/api/projects/${id}/realtime`),
};

export type VitalP75 = {
  samples: number;
  lcp: number | null; cls: number | null; inp: number | null;
  ttfb: number | null; fcp: number | null;
};
export type VitalPage = { path: string; samples: number; lcp: number | null; inp: number | null; cls: number | null };
export type VitalsResult = { range: string; p75: VitalP75; pages: VitalPage[] };

export type Realtime = {
  visitors: number;
  perMinute: { t: string; pageviews: number }[];
  recent: { type: string; name: string | null; path: string | null; country: string | null; ts: string }[];
  topPaths: { path: string; views: number }[];
};

export type ReleaseRow = {
  version: string;
  created_at: string;
  issues: number;
  events: number;
  last_error: string | null;
};
export type Monitor = {
  id: string;
  name: string;
  url: string;
  method: string;
  interval_seconds: number;
  expected_status: number;
  enabled: boolean;
  last_ok: boolean | null;
  last_ts: string | null;
  uptime_24h: number | null;
  avg_ms: number | null;
};
export type NewMonitor = {
  name: string;
  url: string;
  interval_seconds?: number;
  expected_status?: number;
};
export type FunnelStep = { kind: "event" | "path"; value: string; label?: string };
export type Funnel = { id: string; name: string; steps: FunnelStep[]; created_at: string };
export type FunnelResult = {
  name: string;
  range: string;
  steps: { label: string; kind: string; value: string; count: number; pctOfStart: number; pctOfPrev: number }[];
};

export type IssueStatus = "unresolved" | "resolved" | "ignored";
export type IssueRow = {
  id: string;
  type: string | null;
  title: string;
  culprit: string | null;
  level: string;
  status: IssueStatus;
  count: number;
  first_seen: string;
  last_seen: string;
};
export type Frame = {
  function: string | null;
  file: string | null;
  line: number | null;
  col: number | null;
  original?: { file: string; line: number; col: number; name?: string | null } | null;
};
export type ErrorEvent = {
  id: number;
  message: string | null;
  type: string | null;
  frames: Frame[] | null;
  release: string | null;
  environment: string | null;
  url: string | null;
  anon_id: string | null;
  ts: string;
};
export type IssueDetail = {
  group: IssueRow & { project_id: string };
  events: ErrorEvent[];
  affectedUsers: number;
  releases: string[];
};
